import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';

const STATUS_CFG = {
  pending:    { label: 'Gözləyir',   color: '#F59E0B', bg: '#FEF3C7' },
  preparing:  { label: 'Hazırlanır', color: '#3B82F6', bg: '#EFF6FF' },
  ready:      { label: 'Hazırdır',   color: '#10B981', bg: '#D1FAE5' },
  done:       { label: 'Tamamlandı', color: '#6B7280', bg: '#F3F4F6' },
  cancelled:  { label: 'Ləğv edildi',color: '#EF4444', bg: '#FEE2E2' },
};

const NEXT_STATUS = {
  pending:   'preparing',
  preparing: 'ready',
  ready:     'done',
};

function timeAgo(val) {
  if (!val) return '';
  const m = Math.floor((Date.now() - new Date(val).getTime()) / 60000);
  if (m < 1)  return 'İndicə';
  if (m < 60) return `${m} dəq əvvəl`;
  return `${Math.floor(m / 60)} saat əvvəl`;
}

// ─── Print helpers ────────────────────────────────────────────────────────────
function printKitchenTickets(businessName, tableNumber, items, categories) {
  // Group items by station
  const stationMap = {};
  items.forEach(item => {
    const cat = categories.find(c => c.id === item.category_id);
    const station = cat?.station || 'Mətbəx';
    if (!stationMap[station]) stationMap[station] = [];
    stationMap[station].push(item);
  });

  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const dateStr = `${String(now.getDate()).padStart(2,'0')}.${String(now.getMonth()+1).padStart(2,'0')}.${now.getFullYear()}`;

  const stations = Object.entries(stationMap);
  if (stations.length === 0) return;

  const ticketsHtml = stations.map(([station, stItems]) => `
    <div class="ticket">
      <div class="station">${station.toUpperCase()}</div>
      <div class="table-info">Masa: <strong>#${tableNumber}</strong></div>
      <div class="time-info">${timeStr} &nbsp;|&nbsp; ${dateStr}</div>
      <div class="divider">--------------------------------</div>
      ${stItems.map(it => `
        <div class="item-row">
          <span class="item-qty">${it.quantity}×</span>
          <span class="item-name">${it.name}${it.variant ? ` (${it.variant})` : ''}</span>
        </div>
        ${it.note ? `<div class="item-note">  ⚠ ${it.note}</div>` : ''}
      `).join('')}
      <div class="divider">--------------------------------</div>
    </div>
  `).join('');

  const win = window.open('', '_blank', 'width=320,height=700');
  win.document.write(`
    <html><head><title>Stansiya Cekleri</title>
    <style>
      @page { size: 72mm auto; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; font-size: 12px; background: #e5e5e5; width: 100%; display: flex; justify-content: center; padding: 12px 0; }
      @media print { body { background: #fff; padding: 0; display: block; } }
      .ticket { width: 72mm; padding: 4mm 3mm; page-break-after: always; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.15); margin-bottom: 10px; }
      .station { font-size: 16px; font-weight: bold; text-align: center; margin-bottom: 3px; letter-spacing: 2px; text-transform: uppercase; }
      .table-info { font-size: 14px; font-weight: bold; text-align: center; margin: 2px 0; }
      .time-info { font-size: 10px; text-align: center; color: #555; margin-bottom: 5px; }
      .divider { font-size: 10px; color: #888; margin: 4px 0; }
      .item-row { display: flex; gap: 5px; margin: 4px 0; font-size: 13px; align-items: flex-start; }
      .item-qty { font-weight: bold; min-width: 22px; flex-shrink: 0; }
      .item-name { flex: 1; word-break: break-word; line-height: 1.3; font-weight: bold; }
      .item-note { font-size: 10px; color: #333; padding-left: 27px; margin-top: 1px; font-style: italic; }
      @media print {
        .ticket { page-break-after: always; }
        body { margin: 0; }
      }
    </style>
    </head><body>
      ${ticketsHtml}
      <script>window.onload = function(){ window.print(); setTimeout(()=>window.close(), 1000); }<\/script>
    </body></html>
  `);
  win.document.close();
}

function printCustomerReceipt(businessName, tableNumber, orders) {
  const allItems = orders.flatMap(o => Array.isArray(o.items) ? o.items : []);
  const total = orders.reduce((s, o) => s + (o.total || 0), 0);
  const now = new Date();
  const timeStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const dateStr = `${String(now.getDate()).padStart(2,'0')}.${String(now.getMonth()+1).padStart(2,'0')}.${now.getFullYear()}`;

  const win = window.open('', '_blank', 'width=320,height=700');
  win.document.write(`
    <html><head><title>Hesab</title>
    <style>
      @page { size: 72mm auto; margin: 0; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Courier New', monospace; font-size: 12px; background: #e5e5e5; width: 100%; display: flex; justify-content: center; padding: 12px 0; }
      @media print { body { background: #fff; padding: 0; display: block; } }
      .receipt { width: 72mm; padding: 4mm 3mm; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
      .header { text-align: center; margin-bottom: 6px; }
      .biz-name { font-size: 15px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; }
      .sub { font-size: 10px; color: #444; margin-top: 2px; }
      .divider { font-size: 10px; color: #888; margin: 5px 0; letter-spacing: 0; }
      .item-row { display: flex; justify-content: space-between; align-items: flex-start; margin: 3px 0; font-size: 11px; gap: 4px; }
      .item-left { flex: 1; word-break: break-word; line-height: 1.3; }
      .item-qty { font-weight: bold; min-width: 18px; }
      .item-price { font-weight: bold; white-space: nowrap; text-align: right; min-width: 48px; }
      .total-row { display: flex; justify-content: space-between; font-size: 14px; font-weight: bold; margin-top: 6px; border-top: 1px solid #000; padding-top: 4px; }
      .footer { text-align: center; font-size: 10px; color: #666; margin-top: 8px; line-height: 1.5; }
      @media print { body { margin: 0; } }
    </style>
    </head><body>
      <div class="receipt">
        <div class="header">
          <div class="biz-name">${businessName}</div>
          <div class="sub">Masa: #${tableNumber}</div>
          <div class="sub">${dateStr} &nbsp; ${timeStr}</div>
        </div>
        <div class="divider">================================</div>
        ${allItems.map(it => `
          <div class="item-row">
            <span class="item-qty">${it.quantity}x</span>
            <span class="item-left">${it.name}${it.variant ? ` (${it.variant})` : ''}</span>
            <span class="item-price">${((it.price || 0) * it.quantity).toFixed(2)}AZN</span>
          </div>
        `).join('')}
        <div class="divider">--------------------------------</div>
        <div class="total-row">
          <span>CEMI:</span>
          <span>${total.toFixed(2)} AZN</span>
        </div>
        <div class="divider">================================</div>
        <div class="footer">Tesekkur edirik!<br>${businessName}</div>
      </div>
      <script>window.onload = function(){ window.print(); setTimeout(()=>window.close(), 1000); }<\/script>
    </body></html>
  `);
  win.document.close();
}

// ─── Mövcud Sifarişə Əlavə Modal ─────────────────────────────────────────────
function AddItemsModal({ order, categories, menuItems, onClose, onAdded }) {
  const [activeCat, setActiveCat] = useState(categories[0]?.id || null);
  const [cart, setCart]           = useState([]);
  const [saving, setSaving]       = useState(false);

  const catItems = menuItems.filter(i => i.category_id === activeCat && i.is_available !== false);
  const total    = cart.reduce((s, c) => s + c.price * c.quantity, 0);

  const addToCart = (item) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === item.id && !c.variant);
      if (ex) return prev.map(c => c.id === item.id && !c.variant ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: parseFloat(item.price) || 0, quantity: 1, note: '', variant: '', category_id: item.category_id }];
    });
  };

  const addVariant = (item, variant) => {
    setCart(prev => {
      const ex = prev.find(c => c.id === item.id && c.variant === variant.label);
      if (ex) return prev.map(c => c.id === item.id && c.variant === variant.label ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: parseFloat(variant.price) || 0, quantity: 1, note: '', variant: variant.label, category_id: item.category_id }];
    });
  };

  const updateQty = (idx, delta) => {
    setCart(prev => prev.map((c, i) => i === idx ? { ...c, quantity: c.quantity + delta } : c).filter(c => c.quantity > 0));
  };

  const handleSave = async () => {
    if (cart.length === 0) return;
    setSaving(true);
    try {
      const existingItems = Array.isArray(order.items) ? order.items : [];
      const newItems = [...existingItems, ...cart.map(c => ({
        id: c.id, name: c.name, price: c.price,
        quantity: c.quantity, note: c.note, variant: c.variant,
        category_id: c.category_id,
      }))];
      const newTotal = newItems.reduce((s, i) => s + (i.price || 0) * i.quantity, 0);
      const { error } = await supabase.from('orders')
        .update({ items: newItems, total: newTotal })
        .eq('id', order.id);
      if (error) throw error;
      onAdded(cart, order.table_number, categories);
      onClose();
    } catch (err) {
      alert('Xəta: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 640,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>
              ➕ Masa #{order.table_number} — Əlavə Sifariş
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
              Mövcud sifarişə item əlavə et
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none',
            borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 15 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* Sol: Menyu */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #f0f0f0' }}>
            {/* Kateqoriya tabları */}
            <div style={{ display: 'flex', gap: 6, padding: '10px 14px', overflowX: 'auto',
              borderBottom: '1px solid #f5f5f5', flexShrink: 0 }}>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setActiveCat(cat.id)}
                  style={{ padding: '5px 12px', borderRadius: 20, border: '1.5px solid',
                    borderColor: activeCat === cat.id ? '#f97316' : '#e5e7eb',
                    background: activeCat === cat.id ? '#fff7ed' : '#fff',
                    color: activeCat === cat.id ? '#f97316' : '#6b7280',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {cat.name}
                </button>
              ))}
            </div>
            {/* Items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {catItems.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 10px', borderRadius: 9, border: '1.5px solid #f0f0f0', background: '#fafafa', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{item.name}</div>
                    <div style={{ fontSize: 11, color: '#f97316', fontWeight: 700, marginTop: 1 }}>
                      {item.variants?.length > 0
                        ? item.variants.map(v => `${v.label}: ${parseFloat(v.price).toFixed(2)}₼`).join(' · ')
                        : `${parseFloat(item.price).toFixed(2)}₼`}
                    </div>
                  </div>
                  {item.variants?.length > 0 ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {item.variants.map(v => (
                        <button key={v.label} onClick={() => addVariant(item, v)}
                          style={{ padding: '4px 7px', borderRadius: 6, border: 'none',
                            background: '#f97316', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          +{v.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button onClick={() => addToCart(item)}
                      style={{ padding: '5px 12px', borderRadius: 7, border: 'none',
                        background: '#f97316', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      + Əlavə
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sağ: Seçilənlər */}
          <div style={{ width: 220, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f5f5f5',
              fontSize: 11, fontWeight: 700, color: '#6b7280' }}>
              ƏLAVƏ İTEMLƏR ({cart.length})
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#d1d5db', fontSize: 12 }}>
                  Hələ seçilməyib
                </div>
              ) : cart.map((c, idx) => (
                <div key={idx} style={{ padding: '7px 9px', borderRadius: 8, background: '#f9fafb', border: '1px solid #f0f0f0' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e' }}>
                    {c.name}{c.variant ? ` (${c.variant})` : ''}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                    <button onClick={() => updateQty(idx, -1)}
                      style={{ width: 22, height: 22, borderRadius: 5, border: '1px solid #e5e7eb',
                        background: '#fff', cursor: 'pointer', fontSize: 13, color: '#ef4444' }}>−</button>
                    <span style={{ fontSize: 12, fontWeight: 700, minWidth: 14, textAlign: 'center' }}>{c.quantity}</span>
                    <button onClick={() => updateQty(idx, 1)}
                      style={{ width: 22, height: 22, borderRadius: 5, border: '1px solid #e5e7eb',
                        background: '#fff', cursor: 'pointer', fontSize: 13, color: '#10b981' }}>+</button>
                    <span style={{ fontSize: 11, color: '#f97316', fontWeight: 700, marginLeft: 'auto' }}>
                      {(c.price * c.quantity).toFixed(2)}₼
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: '10px 12px', borderTop: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 700 }}>Əlavə:</span>
                <span style={{ fontSize: 14, fontWeight: 800 }}>{total.toFixed(2)}₼</span>
              </div>
              <button onClick={handleSave} disabled={saving || cart.length === 0}
                style={{ width: '100%', padding: '10px', borderRadius: 9, border: 'none',
                  background: cart.length > 0 ? '#f97316' : '#d1d5db',
                  color: '#fff', fontWeight: 800, fontSize: 14,
                  cursor: cart.length > 0 ? 'pointer' : 'not-allowed' }}>
                {saving ? 'Göndərilir...' : '🖨️ Göndər'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Yeni Sifariş Modal ───────────────────────────────────────────────────────
function NewOrderModal({ business, locationId, categories, menuItems, tables, activeOrders, onClose, onSubmit }) {
  const [tableNum, setTableNum]     = useState('');
  const [customTable, setCustomTable] = useState('');
  const [cart, setCart]             = useState([]);
  const [activeCat, setActiveCat]   = useState(categories[0]?.id || null);
  const [orderNote, setOrderNote]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  const catItems = menuItems.filter(i => i.category_id === activeCat && i.is_available !== false);

  const addToCart = (item) => {
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id && !c.variant);
      if (existing) return prev.map(c => c.id === item.id && !c.variant ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: parseFloat(item.price) || 0, quantity: 1, note: '', variant: '', category_id: item.category_id }];
    });
  };

  const addVariantToCart = (item, variant) => {
    const key = item.id + variant.label;
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id && c.variant === variant.label);
      if (existing) return prev.map(c => c.id === item.id && c.variant === variant.label ? { ...c, quantity: c.quantity + 1 } : c);
      return [...prev, { id: item.id, name: item.name, price: parseFloat(variant.price) || 0, quantity: 1, note: '', variant: variant.label, category_id: item.category_id }];
    });
  };

  const updateQty = (idx, delta) => {
    setCart(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + delta };
      return updated.filter(c => c.quantity > 0);
    });
  };

  const updateNote = (idx, note) => {
    setCart(prev => prev.map((c, i) => i === idx ? { ...c, note } : c));
  };

  const total = cart.reduce((s, c) => s + c.price * c.quantity, 0);
  const resolvedTable = tableNum === '__custom__' ? customTable : tableNum;

  // Bu masa üçün aktiv sifariş varmı?
  const tableActiveOrders = resolvedTable
    ? (activeOrders || []).filter(o =>
        String(o.table_number) === String(resolvedTable) &&
        ['pending', 'preparing', 'ready'].includes(o.status))
    : [];

  const handleSubmit = async () => {
    if (!resolvedTable) { alert('Masa seçin'); return; }
    if (cart.length === 0) { alert('Ən az bir məhsul seçin'); return; }
    setSubmitting(true);
    const items = cart.map(c => ({
      id: c.id, name: c.name, price: c.price,
      quantity: c.quantity, note: c.note, variant: c.variant,
      category_id: c.category_id,
    }));
    try {
      const { error } = await supabase.from('orders').insert({
        business_id: business.id,
        location_id: locationId || null,
        table_number: resolvedTable,
        items,
        total,
        status: 'pending',
        note: orderNote || null,
      });
      if (error) throw error;
      onSubmit(items, resolvedTable, categories);
      onClose();
    } catch (err) {
      alert('Xəta: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 720,
        maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>➕ Yeni Sifariş</div>
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>Masa seç → məhsul əlavə et → göndər</div>
          </div>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8,
            width: 34, height: 34, cursor: 'pointer', fontSize: 16, color: '#6b7280' }}>✕</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

          {/* Sol: Masa + Menyu */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid #f0f0f0' }}>

            {/* Masa seçimi */}
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #f5f5f5' }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: 6 }}>MASA</label>
              <select value={tableNum} onChange={e => setTableNum(e.target.value)}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                  fontSize: 14, background: '#fff', color: '#1a1a2e' }}>
                <option value=''>— Masa seçin —</option>
                {tables.map(t => (
                  <option key={t.id} value={t.table_number}>Masa #{t.table_number}{t.zone ? ` (${t.zone})` : ''}</option>
                ))}
                <option value='__custom__'>✏️ Əl ilə yaz</option>
              </select>
              {tableNum === '__custom__' && (
                <input value={customTable} onChange={e => setCustomTable(e.target.value)}
                  placeholder="Masa nömrəsi..." autoFocus
                  style={{ marginTop: 8, width: '100%', padding: '9px 12px', borderRadius: 8,
                    border: '1.5px solid #f97316', fontSize: 14, boxSizing: 'border-box' }} />
              )}

              {/* Aktiv sifariş xəbərdarlığı */}
              {tableActiveOrders.length > 0 && (
                <div style={{ marginTop: 10, background: '#fffbeb', borderRadius: 8,
                  padding: '10px 12px', border: '1.5px solid #fcd34d' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>
                    ⚠️ Masa #{resolvedTable}-də {tableActiveOrders.length} aktiv sifariş var
                  </div>
                  <div style={{ fontSize: 11, color: '#78350f', lineHeight: 1.5 }}>
                    Mövcud sifarişə əlavə etmək üçün sifariş kartındakı <strong>"+ Əlavə"</strong> düyməsini istifadə edin.
                    Yeni sifariş yaratmaq istəyirsinizsə davam edin.
                  </div>
                </div>
              )}
            </div>

            {/* Kateqoriya tabları */}
            <div style={{ display: 'flex', gap: 6, padding: '10px 18px', overflowX: 'auto',
              borderBottom: '1px solid #f5f5f5', flexShrink: 0 }}>
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setActiveCat(cat.id)}
                  style={{ padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                    borderColor: activeCat === cat.id ? '#f97316' : '#e5e7eb',
                    background: activeCat === cat.id ? '#fff7ed' : '#fff',
                    color: activeCat === cat.id ? '#f97316' : '#6b7280',
                    fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Məhsullar */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {catItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>Bu kateqoriyada məhsul yoxdur</div>
              ) : catItems.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 12px', borderRadius: 10, border: '1.5px solid #f0f0f0',
                  background: '#fafafa', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{item.name}</div>
                    <div style={{ fontSize: 12, color: '#f97316', fontWeight: 700, marginTop: 2 }}>
                      {item.variants?.length > 0
                        ? item.variants.map(v => `${v.label}: ${parseFloat(v.price).toFixed(2)}₼`).join(' · ')
                        : `${parseFloat(item.price).toFixed(2)}₼`}
                    </div>
                  </div>
                  {item.variants?.length > 0 ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {item.variants.map(v => (
                        <button key={v.label} onClick={() => addVariantToCart(item, v)}
                          style={{ padding: '5px 8px', borderRadius: 7, border: 'none',
                            background: '#f97316', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          +{v.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <button onClick={() => addToCart(item)}
                      style={{ padding: '6px 14px', borderRadius: 8, border: 'none',
                        background: '#f97316', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                      + Əlavə
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Sağ: Səbət */}
          <div style={{ width: 260, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f5f5f5' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280' }}>SİFARİŞ ({cart.length} item)</div>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: '#d1d5db', fontSize: 13 }}>
                  Hələ məhsul seçilməyib
                </div>
              ) : cart.map((c, idx) => (
                <div key={idx} style={{ padding: '8px 10px', borderRadius: 9, background: '#f9fafb', border: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: '#1a1a2e' }}>
                      {c.name}{c.variant ? ` (${c.variant})` : ''}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>
                      {(c.price * c.quantity).toFixed(2)}₼
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                    <button onClick={() => updateQty(idx, -1)}
                      style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e5e7eb',
                        background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#ef4444' }}>−</button>
                    <span style={{ fontSize: 13, fontWeight: 700, minWidth: 16, textAlign: 'center' }}>{c.quantity}</span>
                    <button onClick={() => updateQty(idx, 1)}
                      style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid #e5e7eb',
                        background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#10b981' }}>+</button>
                    <input value={c.note} onChange={e => updateNote(idx, e.target.value)}
                      placeholder="Qeyd..."
                      style={{ flex: 1, padding: '3px 7px', borderRadius: 6, border: '1px solid #e5e7eb',
                        fontSize: 11, outline: 'none' }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Ümumi qeyd + Göndər */}
            <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0' }}>
              <input value={orderNote} onChange={e => setOrderNote(e.target.value)}
                placeholder="Ümumi qeyd (ixtiyari)..."
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
                  fontSize: 12, marginBottom: 10, boxSizing: 'border-box', outline: 'none' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#1a1a2e' }}>CƏMİ:</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>{total.toFixed(2)}₼</span>
              </div>
              <button onClick={handleSubmit} disabled={submitting || cart.length === 0 || !resolvedTable}
                style={{ width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                  background: cart.length > 0 && resolvedTable ? '#f97316' : '#d1d5db',
                  color: '#fff', fontWeight: 800, fontSize: 15, cursor: cart.length > 0 && resolvedTable ? 'pointer' : 'not-allowed' }}>
                {submitting ? 'Göndərilir...' : '🖨️ Mətbəxə Göndər'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Kassa Tab ────────────────────────────────────────────────────────────────
function KassaTab({ business, locationId, businessName }) {
  const [activeOrders, setActiveOrders] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [closing, setClosing] = useState(false);

  const load = useCallback(async () => {
    if (!business?.id) return;
    let q = supabase.from('orders').select('*')
      .eq('business_id', business.id)
      .in('status', ['pending', 'preparing', 'ready'])
      .order('created_at', { ascending: true });
    if (locationId) q = q.eq('location_id', locationId);
    const { data } = await q;
    setActiveOrders(data || []);
  }, [business?.id, locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!business?.id) return;
    const ch = supabase.channel('kassa-rt').on('postgres_changes',
      { event: '*', schema: 'public', table: 'orders', filter: `business_id=eq.${business.id}` },
      () => load()
    ).subscribe();
    return () => supabase.removeChannel(ch);
  }, [business?.id, load]);

  // Group by table
  const tableMap = {};
  activeOrders.forEach(o => {
    const t = String(o.table_number || '—');
    if (!tableMap[t]) tableMap[t] = [];
    tableMap[t].push(o);
  });

  const tableList = Object.entries(tableMap).map(([num, orders]) => ({
    num,
    orders,
    total: orders.reduce((s, o) => s + (o.total || 0), 0),
    items: orders.flatMap(o => Array.isArray(o.items) ? o.items : []),
  }));

  const selected = tableList.find(t => t.num === selectedTable);

  const closeTable = async () => {
    if (!selected) return;
    setClosing(true);
    const ids = selected.orders.map(o => o.id);
    await supabase.from('orders').update({ status: 'done' }).in('id', ids);
    setSelectedTable(null);
    setClosing(false);
  };

  const printBill = () => {
    if (!selected) return;
    printCustomerReceipt(businessName, selected.num, selected.orders);
  };

  if (tableList.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🏁</div>
        <div style={{ fontSize: 15, color: '#9ca3af', fontWeight: 600 }}>Aktiv masa yoxdur</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* Masa siyahısı */}
      <div style={{ flex: '0 0 auto', minWidth: 200 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
          Aktiv Masalar ({tableList.length})
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tableList.map(t => (
            <button key={t.num} onClick={() => setSelectedTable(t.num)}
              style={{ padding: '14px 18px', borderRadius: 12, border: '2px solid',
                borderColor: selectedTable === t.num ? '#f97316' : '#e5e7eb',
                background: selectedTable === t.num ? '#fff7ed' : '#fff',
                cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>Masa #{t.num}</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#f97316', marginTop: 2 }}>{t.total.toFixed(2)}₼</div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{t.items.length} item</div>
            </button>
          ))}
        </div>
      </div>

      {/* Seçili masa hesabı */}
      {selected && (
        <div style={{ flex: 1, minWidth: 260, background: '#fff', borderRadius: 16,
          border: '1.5px solid #f0f0f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

          <div style={{ padding: '16px 20px', background: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>Masa #{selected.num} — Hesab</div>
          </div>

          <div style={{ padding: '12px 20px' }}>
            {selected.items.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                padding: '6px 0', borderBottom: i < selected.items.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                <span style={{ fontSize: 13, color: '#1a1a2e' }}>
                  {item.quantity}× {item.name}{item.variant ? ` (${item.variant})` : ''}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e' }}>
                  {((item.price || 0) * item.quantity).toFixed(2)}₼
                </span>
              </div>
            ))}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0 4px', marginTop: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: '#1a1a2e' }}>CƏMİ:</span>
              <span style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{selected.total.toFixed(2)}₼</span>
            </div>
          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 10 }}>
            <button onClick={printBill}
              style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #f97316',
                background: '#fff', color: '#f97316', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              🖨️ Çap Et
            </button>
            <button onClick={closeTable} disabled={closing}
              style={{ flex: 1, padding: '11px', borderRadius: 10, border: 'none',
                background: '#10b981', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              {closing ? 'Bağlanır...' : '✓ Ödədi / Bağla'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Occupied Tables Section ───────────────────────────────────────────────────
function OccupiedTablesSection({ business, locationId, orders }) {
  const [tables, setTables] = useState([]);

  useEffect(() => {
    if (!business?.id) return;
    let q = supabase.from('tables').select('id, table_number, occupied_since, current_user_name, status')
      .eq('business_id', business.id).eq('status', 'occupied').eq('is_active', true).order('table_number');
    if (locationId) q = q.eq('business_location_id', locationId);
    q.then(({ data }) => setTables(data || []));
  }, [business?.id, locationId]);

  useEffect(() => {
    if (!business?.id) return;
    const ch = supabase.channel('occupied-rt-' + business.id)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tables',
        filter: `business_id=eq.${business.id}` }, ({ new: t }) => {
        if (t.status === 'occupied') {
          setTables(prev => {
            const exists = prev.find(x => x.id === t.id);
            const updated = exists ? prev.map(x => x.id === t.id ? { ...x, ...t } : x) : [...prev, t];
            return updated.sort((a, b) => a.table_number - b.table_number);
          });
        } else {
          setTables(prev => prev.filter(x => x.id !== t.id));
        }
      }).subscribe();
    return () => supabase.removeChannel(ch);
  }, [business?.id]);

  if (tables.length === 0) return null;

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 10 }}>
        Dolu Masalar ({tables.length})
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {tables.map(table => {
          const tableOrders = orders.filter(o =>
            String(o.table_number) === String(table.table_number) &&
            ['pending', 'preparing', 'ready'].includes(o.status)
          );
          return (
            <div key={table.id} style={{ background: '#fff', borderRadius: 14, border: '2px solid #e0f2fe', overflow: 'hidden' }}>
              <div style={{ background: '#f0f9ff', padding: '12px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>📍</span>
                  <div>
                    <span style={{ fontWeight: 800, fontSize: 15, color: '#0369a1' }}>Masa #{table.table_number} açıldı</span>
                    {table.current_user_name && <span style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>· {table.current_user_name}</span>}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{timeAgo(table.occupied_since)}</span>
              </div>
              {tableOrders.length === 0 ? (
                <div style={{ padding: '12px 18px', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Hələki sifariş yoxdur</div>
              ) : (
                <div style={{ padding: '10px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {tableOrders.map(o => {
                    const cfg = STATUS_CFG[o.status] || STATUS_CFG.pending;
                    const items = Array.isArray(o.items) ? o.items : [];
                    return (
                      <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '8px 12px', borderRadius: 9, background: cfg.bg, gap: 10 }}>
                        <div style={{ flex: 1, fontSize: 13, color: '#1a1a2e' }}>
                          {items.map((it, i) => <span key={i}>{it.quantity}× {it.name}{i < items.length - 1 ? ', ' : ''}</span>)}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, padding: '2px 8px', borderRadius: 8, background: '#fff', whiteSpace: 'nowrap' }}>{cfg.label}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap' }}>{(o.total || 0).toFixed(2)}₼</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── QR Kodlar Tab ────────────────────────────────────────────────────────────
function QRCodesTab({ business, locationId }) {
  const [tables, setTables]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!business?.id) return;
    let q = supabase.from('tables').select('id, table_number, zone, capacity, status')
      .eq('business_id', business.id).eq('is_active', true).order('table_number');
    if (locationId) q = q.eq('business_location_id', locationId);
    q.then(({ data }) => { setTables(data || []); setLoading(false); });
  }, [business?.id, locationId]);

  const menuId  = locationId || business?.id;
  const baseUrl = menuId ? `https://cafegoo.com/menu/${menuId}` : '';
  const tableUrl = (num) => `${baseUrl}?table=${encodeURIComponent(num)}`;
  const qrSrc = (num) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(tableUrl(num))}&bgcolor=ffffff&color=1a1a2e&margin=16`;

  const downloadQR = async (num) => {
    const url = qrSrc(num);
    try {
      const res  = await fetch(url);
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `masa-${num}-qr.png`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch { window.open(url, '_blank'); }
  };

  const downloadAll = async () => {
    for (const t of tables) { await downloadQR(t.table_number); await new Promise(r => setTimeout(r, 300)); }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Yüklənir...</div>;
  if (tables.length === 0) return (
    <div style={{ textAlign: 'center', padding: 60 }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🗂️</div>
      <div style={{ fontSize: 15, color: '#9ca3af', fontWeight: 600 }}>Floor plan-da aktiv masa yoxdur</div>
      <div style={{ fontSize: 13, color: '#d1d5db', marginTop: 6 }}>Əvvəlcə Floor Plan səhifəsindən masa əlavə edin</div>
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <button onClick={downloadAll} style={{ padding: '8px 18px', borderRadius: 10, border: 'none',
          background: '#1a1a2e', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          ⬇️ Hamısını yüklə
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
        {tables.map(table => {
          const st = STATUS_CFG[table.status] || STATUS_CFG.pending;
          return (
            <div key={table.id} style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
              border: '1.5px solid #f0f0f0', padding: '18px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <img src={qrSrc(table.table_number)} alt={`Masa ${table.table_number}`}
                style={{ width: 130, height: 130, borderRadius: 8, border: '1px solid #f0f0f0' }} />
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#1a1a2e' }}>Masa #{table.table_number}</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{table.capacity} nəfərlik</div>
                <span style={{ display: 'inline-block', marginTop: 6, padding: '2px 10px', borderRadius: 20,
                  fontSize: 11, fontWeight: 700, background: st.bg, color: st.color }}>{st.label}</span>
              </div>
              <button onClick={() => downloadQR(table.table_number)}
                style={{ width: '100%', padding: '8px 0', borderRadius: 9, border: 'none',
                  background: '#f97316', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                ⬇️ QR Yüklə
              </button>
              <div style={{ fontSize: 9, color: '#d1d5db', wordBreak: 'break-all', textAlign: 'center' }}>
                {tableUrl(table.table_number)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Hesabat Tab ──────────────────────────────────────────────────────────────
function HesabatTab({ business, locationId }) {
  const [period,   setPeriod]  = useState('month');  // today | week | month | custom
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [orders,   setOrders]  = useState([]);
  const [loading,  setLoading] = useState(false);

  const getRange = () => {
    const now   = new Date();
    const today = now.toISOString().slice(0, 10);
    if (period === 'today') return { from: today, to: today };
    if (period === 'week') {
      const d = new Date(now); d.setDate(d.getDate() - 6);
      return { from: d.toISOString().slice(0, 10), to: today };
    }
    if (period === 'month') {
      return { from: `${today.slice(0, 7)}-01`, to: today };
    }
    return { from: dateFrom, to: dateTo };
  };

  const load = React.useCallback(async () => {
    if (!business?.id) return;
    const { from, to } = getRange();
    if (!from || !to) return;
    setLoading(true);
    let q = supabase.from('orders').select('id, items, total, status, created_at, table_number')
      .eq('business_id', business.id)
      .neq('status', 'cancelled')
      .gte('created_at', from + 'T00:00:00')
      .lte('created_at', to   + 'T23:59:59')
      .order('created_at', { ascending: false });
    if (locationId) q = q.eq('location_id', locationId);
    const { data } = await q;
    setOrders(data || []);
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, locationId, period, dateFrom, dateTo]);

  React.useEffect(() => { load(); }, [load]);

  // Items aggregate
  const itemTotals = React.useMemo(() => {
    const map = {};
    orders.forEach(order => {
      const items = Array.isArray(order.items) ? order.items : [];
      items.forEach(it => {
        const key = it.name + (it.variant ? `__${it.variant}` : '');
        if (!map[key]) map[key] = { name: it.name, variant: it.variant || '', qty: 0, revenue: 0 };
        map[key].qty     += (it.quantity || 1);
        map[key].revenue += (it.price || 0) * (it.quantity || 1);
      });
    });
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [orders]);

  const totalRevenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const totalOrders  = orders.length;
  const maxQty       = itemTotals[0]?.qty || 1;

  const { from, to } = getRange();
  const rangeLabel = from && to && from !== to ? `${from} — ${to}` : from || '';

  return (
    <div style={{ maxWidth: 700 }}>

      {/* Period seçici */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { key: 'today', label: 'Bugün' },
          { key: 'week',  label: 'Son 7 gün' },
          { key: 'month', label: 'Bu ay' },
          { key: 'custom',label: 'Tarix seç' },
        ].map(p => (
          <button key={p.key} onClick={() => setPeriod(p.key)} style={{
            padding: '7px 16px', borderRadius: 20, border: '1.5px solid',
            borderColor: period === p.key ? '#f97316' : '#e5e7eb',
            background:  period === p.key ? '#fff7ed' : '#fff',
            color:        period === p.key ? '#f97316' : '#6b7280',
            fontWeight: 700, fontSize: 13, cursor: 'pointer',
          }}>{p.label}</button>
        ))}
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13 }} />
            <span style={{ color: '#9ca3af', fontSize: 13 }}>—</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 13 }} />
          </div>
        )}
      </div>

      {/* Xülasə kartlar */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Sifariş sayı',    value: totalOrders,            icon: '🛒' },
          { label: 'Ümumi dövriyyə',  value: `${totalRevenue.toFixed(2)} ₼`, icon: '💰' },
          { label: 'Unikal məhsul',   value: itemTotals.length,      icon: '📋' },
        ].map((s, i) => (
          <div key={i} style={{ flex: 1, minWidth: 120, background: '#fff', borderRadius: 12,
            padding: '12px 16px', border: '1.5px solid #f0f0f0', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{s.label}</div>
            {i === 0 && rangeLabel && <div style={{ fontSize: 10, color: '#d1d5db', marginTop: 2 }}>{rangeLabel}</div>}
          </div>
        ))}
      </div>

      {/* Məhsul siyahısı */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Yüklənir...</div>
      ) : itemTotals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 15, color: '#9ca3af', fontWeight: 600 }}>Bu dövrdə sifariş yoxdur</div>
        </div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 14, border: '1.5px solid #f0f0f0', overflow: 'hidden' }}>
          {/* Başlıq */}
          <div style={{ display: 'flex', padding: '10px 18px', background: '#f8f9fa',
            borderBottom: '1px solid #f0f0f0', gap: 8 }}>
            <div style={{ flex: 1, fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>Məhsul adı</div>
            <div style={{ width: 70, fontSize: 11, fontWeight: 800, color: '#9ca3af', textAlign: 'right', textTransform: 'uppercase' }}>Miqdar</div>
            <div style={{ width: 80, fontSize: 11, fontWeight: 800, color: '#9ca3af', textAlign: 'right', textTransform: 'uppercase' }}>Cəmi</div>
          </div>

          {itemTotals.map((item, idx) => {
            const pct = Math.round((item.qty / maxQty) * 100);
            return (
              <div key={idx} style={{ padding: '11px 18px', borderBottom: idx < itemTotals.length - 1 ? '1px solid #f5f5f5' : 'none',
                display: 'flex', alignItems: 'center', gap: 8 }}>

                {/* Sıra nömrəsi */}
                <div style={{ width: 22, fontSize: 11, color: '#d1d5db', fontWeight: 700, flexShrink: 0 }}>
                  {idx + 1}
                </div>

                {/* Ad + bar */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.name}{item.variant ? <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 5 }}>({item.variant})</span> : ''}
                  </div>
                  {/* Progress bar */}
                  <div style={{ height: 4, borderRadius: 2, background: '#f0f0f0', marginTop: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2,
                      background: idx === 0 ? '#f97316' : idx < 3 ? '#fb923c' : '#fed7aa',
                      transition: 'width 0.3s' }} />
                  </div>
                </div>

                {/* Miqdar badge */}
                <div style={{ width: 70, textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>{item.qty}</span>
                  <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 2 }}>ədəd</span>
                </div>

                {/* Cəmi */}
                <div style={{ width: 80, textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#6b7280', flexShrink: 0 }}>
                  {item.revenue.toFixed(2)} ₼
                </div>
              </div>
            );
          })}

          {/* Footer cəm */}
          <div style={{ padding: '12px 18px', background: '#f8f9fa', borderTop: '2px solid #f0f0f0',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#6b7280' }}>
              {itemTotals.reduce((s, i) => s + i.qty, 0)} məhsul · {totalOrders} sifariş
            </span>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#1a1a2e' }}>
              {totalRevenue.toFixed(2)} ₼
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab({ business, locationId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [saved,  setSaved]      = useState(false);

  const COLS = 'table_track_enabled, qr_menu_enabled, orders_enabled, scan_enabled, scan_opens_menu';

  useEffect(() => {
    if (!business?.id) return;
    const table = locationId ? 'business_locations' : 'businesses';
    const id    = locationId || business.id;
    supabase.from(table).select(COLS).eq('id', id).maybeSingle().then(({ data }) => {
      setSettings({
        table_track_enabled: data?.table_track_enabled ?? true,
        qr_menu_enabled:     data?.qr_menu_enabled     ?? true,
        orders_enabled:      data?.orders_enabled      ?? true,
        scan_enabled:        data?.scan_enabled        ?? false,
        scan_opens_menu:     data?.scan_opens_menu     ?? true,
      });
    });
  }, [business?.id, locationId]);

  const set = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  const save = async () => {
    if (!business?.id) return;
    setSaving(true);
    const table = locationId ? 'business_locations' : 'businesses';
    const id    = locationId || business.id;
    await supabase.from(table).update(settings).eq('id', id);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!settings) return <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Yüklənir...</div>;

  const Toggle = ({ value, onChange, color = '#f97316' }) => (
    <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24, flexShrink: 0, cursor: 'pointer' }}>
      <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
      <span style={{ position: 'absolute', inset: 0, borderRadius: 24, transition: '0.2s', background: value ? color : '#d1d5db' }}>
        <span style={{ position: 'absolute', top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: '0.2s' }} />
      </span>
    </label>
  );

  const Row = ({ emoji, bg, title, desc, val, onChange, color, border = true }) => (
    <div style={{ padding: '18px 22px', borderBottom: border ? '1px solid #f5f5f5' : 'none', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 11, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>{emoji}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{title}</div>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>{desc}</div>
      </div>
      <Toggle value={val} onChange={onChange} color={color} />
    </div>
  );

  const GroupLabel = ({ label }) => (
    <div style={{ padding: '14px 22px 8px', fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
  );

  return (
    <div style={{ maxWidth: 540 }}>
      <GroupLabel label="QR Kod davranışı (kamera + app)" />
      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #f0f0f0', overflow: 'hidden', marginBottom: 16 }}>
        <Row emoji="📍" bg="#eff6ff" title="Masa dolu işarələnsin"
          desc={settings.table_track_enabled ? 'QR oxunanda masa avtomatik "Dolu" olur' : 'Masa statusu dəyişmir'}
          val={settings.table_track_enabled} onChange={v => set('table_track_enabled', v)} color="#3b82f6" />
        <Row emoji="📋" bg="#fefce8" title="QR Menü göstərilsin"
          desc={settings.qr_menu_enabled ? 'QR açıldıqda tam menü səhifəsi göstərilir' : 'Sadəcə "Masa aktivləşdirildi" mesajı göstərilir'}
          val={settings.qr_menu_enabled} onChange={v => set('qr_menu_enabled', v)} color="#eab308" />
        <Row emoji="🛒" bg="#fff7ed" title="Online sifariş olsun"
          desc={settings.orders_enabled ? 'Müştərilər QR menüdən sifariş verə bilər' : 'Sifariş sistemi bağlıdır'}
          val={settings.orders_enabled} onChange={v => set('orders_enabled', v)} color="#f97316" border={false} />
      </div>
      <GroupLabel label="Mobile App" />
      <div style={{ background: '#fff', borderRadius: 16, border: '1.5px solid #f0f0f0', overflow: 'hidden', marginBottom: 20 }}>
        <Row emoji="📷" bg="#f0fdf4" title="Masa skan düyməsi görünsün"
          desc={settings.scan_enabled ? 'Appda "Masa Skan et" düyməsi aktifdir' : 'Skan düyməsi gizlidir'}
          val={settings.scan_enabled} onChange={v => set('scan_enabled', v)} color="#10b981" />
        <Row emoji="↗️" bg="#faf5ff" title="Scan etdikdən sonra menü açılsın"
          desc={settings.scan_opens_menu ? 'Scan sonrası QR menü avtomatik açılır' : 'Sadəcə masa işarələnir, menü açılmır'}
          val={settings.scan_opens_menu} onChange={v => set('scan_opens_menu', v)} color="#8b5cf6" border={false} />
      </div>
      <button onClick={save} disabled={saving} style={{ padding: '11px 28px', borderRadius: 10, border: 'none',
        background: saved ? '#10b981' : '#1a1a2e', color: '#fff', fontWeight: 700, fontSize: 14, cursor: saving ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
        {saving ? 'Saxlanır...' : saved ? '✓ Saxlandı' : 'Saxla'}
      </button>
    </div>
  );
}

// ─── Main OrdersPage ──────────────────────────────────────────────────────────
export default function OrdersPage() {
  const { business } = useAuth();
  const { locationId } = useLocation();
  const [orders, setOrders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('active');
  const [showNewOrder, setShowNewOrder]   = useState(false);
  const [addItemsOrder, setAddItemsOrder] = useState(null);

  // Menyu data (sifariş modal üçün)
  const [menuCategories, setMenuCategories] = useState([]);
  const [menuItems, setMenuItems]           = useState([]);
  const [tables, setTables]                 = useState([]);

  const fetchOrders = useCallback(async () => {
    if (!business?.id) return;
    let q = supabase.from('orders').select('*').eq('business_id', business.id).order('created_at', { ascending: false });
    if (locationId) q = q.eq('location_id', locationId);
    if (tab === 'active') q = q.in('status', ['pending', 'preparing', 'ready']);
    else if (tab === 'done') q = q.in('status', ['done', 'cancelled']);
    const { data } = await q.limit(100);
    setOrders(data || []);
    setLoading(false);
  }, [business?.id, locationId, tab]);

  useEffect(() => {
    if (!['qr','settings','kassa','hesabat'].includes(tab)) fetchOrders();
  }, [fetchOrders, tab]);

  useEffect(() => {
    if (!business?.id) return;
    const channel = supabase.channel('orders-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders',
        filter: `business_id=eq.${business.id}` }, () => { if (!['qr','settings','kassa','hesabat'].includes(tab)) fetchOrders(); })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [business?.id, fetchOrders, tab]);

  // Menyu + Masalar yüklə (modal üçün)
  useEffect(() => {
    if (!business?.id) return;
    const loadMenuData = async () => {
      const [catRes, itemRes, tableRes] = await Promise.all([
        supabase.from('menu_categories').select('id, name, station').eq('business_id', business.id).order('sort_order'),
        supabase.from('menu_items').select('id, name, price, variants, category_id, is_available').eq('business_id', business.id),
        supabase.from('tables').select('id, table_number, zone').eq('business_id', business.id).eq('is_active', true).order('table_number'),
      ]);
      setMenuCategories(catRes.data || []);
      setMenuItems(itemRes.data || []);
      setTables(tableRes.data || []);
    };
    loadMenuData();
  }, [business?.id]);

  const updateStatus = async (orderId, status) => {
    await supabase.from('orders').update({ status }).eq('id', orderId);
  };

  const cancel = async (orderId) => {
    await supabase.from('orders').update({ status: 'cancelled' }).eq('id', orderId);
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const activeCount = orders.filter(o => ['pending','preparing','ready'].includes(o.status)).length;

  const TABS = [
    { key: 'active',   label: 'Aktiv' },
    { key: 'kassa',    label: '💵 Kassa' },
    { key: 'done',     label: 'Tamamlanan' },
    { key: 'hesabat',  label: '📊 Hesabat' },
    { key: 'qr',       label: '🔲 QR Kodlar' },
    { key: 'settings', label: '⚙️ Ayarlar' },
  ];

  return (
    <div style={{ padding: '24px 20px', maxWidth: 900, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>
            Sifarişlər
            {activeCount > 0 && tab !== 'qr' && (
              <span style={{ marginLeft: 10, background: '#ef4444', color: '#fff', fontSize: 12, fontWeight: 700, borderRadius: 12, padding: '2px 8px' }}>
                {activeCount}
              </span>
            )}
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            {tab === 'qr' ? 'Masalar üçün QR kodlar' : tab === 'settings' ? 'Sifariş sistemi ayarları' : tab === 'kassa' ? 'Aktiv masalar və hesab' : 'Real-time masa sifarişləri'}
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Yeni Sifariş düyməsi */}
          <button onClick={() => setShowNewOrder(true)}
            style={{ padding: '9px 20px', borderRadius: 10, border: 'none',
              background: '#f97316', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(249,115,22,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}>
            ➕ Yeni Sifariş
          </button>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '7px 14px', borderRadius: 8, border: '1.5px solid',
                borderColor: tab === t.key ? '#f97316' : '#e5e7eb',
                background: tab === t.key ? '#fff7ed' : '#fff',
                color: tab === t.key ? '#f97316' : '#6b7280',
                fontWeight: 700, fontSize: 13, cursor: 'pointer',
              }}>{t.label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Kassa tab */}
      {tab === 'kassa' && (
        <KassaTab business={business} locationId={locationId} businessName={business?.name || 'Restoran'} />
      )}

      {/* QR tab */}
      {tab === 'qr' && <QRCodesTab business={business} locationId={locationId} />}

      {/* Hesabat tab */}
      {tab === 'hesabat' && <HesabatTab business={business} locationId={locationId} />}

      {/* Settings tab */}
      {tab === 'settings' && <SettingsTab business={business} locationId={locationId} />}

      {/* Orders tabs */}
      {!['qr','settings','kassa','hesabat'].includes(tab) && (
        <>
          {tab === 'active' && (
            <OccupiedTablesSection business={business} locationId={locationId} orders={orders} />
          )}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9ca3af' }}>Yüklənir...</div>
          ) : orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🛒</div>
              <div style={{ fontSize: 15, color: '#9ca3af', fontWeight: 600 }}>
                {tab === 'active' ? 'Aktiv sifariş yoxdur' : 'Tamamlanan sifariş yoxdur'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {orders.map(order => {
                const cfg   = STATUS_CFG[order.status] || STATUS_CFG.pending;
                const items = Array.isArray(order.items) ? order.items : [];
                const next  = NEXT_STATUS[order.status];
                return (
                  <div key={order.id} style={{ background: '#fff', borderRadius: 16, padding: '18px 20px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: order.status === 'pending' ? '2px solid #F59E0B' : '1.5px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
                          {order.table_number ? `Masa #${order.table_number}` : 'Masa —'}
                        </span>
                        <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                        {order.source === 'staff' && (
                          <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#f0fdf4', color: '#10b981' }}>👤 Ofisiant</span>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>{formatTime(order.created_at)}</span>
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      {items.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                          padding: '6px 0', borderBottom: idx < items.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
                          <div>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{item.quantity}× {item.name}</span>
                            {item.variant && <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>({item.variant})</span>}
                            {item.note && <div style={{ fontSize: 12, color: '#f97316', marginTop: 2 }}>📝 {item.note}</div>}
                          </div>
                          <span style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', whiteSpace: 'nowrap', marginLeft: 12 }}>
                            {((item.price || 0) * item.quantity).toFixed(2)}₼
                          </span>
                        </div>
                      ))}
                    </div>

                    {order.note && (
                      <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
                        💬 {order.note}
                      </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <span style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>Cəmi: {(order.total || 0).toFixed(2)}₼</span>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {order.status !== 'done' && order.status !== 'cancelled' && (
                          <>
                            <button onClick={() => setAddItemsOrder(order)}
                              style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #fed7aa',
                                background: '#fff7ed', color: '#f97316', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                              ➕ Əlavə
                            </button>
                            <button onClick={() => cancel(order.id)}
                              style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #fecaca',
                                background: '#fff', color: '#ef4444', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                              Ləğv et
                            </button>
                          </>
                        )}
                        {next && (
                          <button onClick={() => updateStatus(order.id, next)}
                            style={{ padding: '7px 16px', borderRadius: 8, border: 'none',
                              background: STATUS_CFG[next].bg, color: STATUS_CFG[next].color,
                              fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            → {STATUS_CFG[next].label}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Mövcud Sifarişə Əlavə Modal */}
      {addItemsOrder && (
        <AddItemsModal
          order={addItemsOrder}
          categories={menuCategories}
          menuItems={menuItems}
          onClose={() => setAddItemsOrder(null)}
          onAdded={(items, tableNumber, cats) => {
            printKitchenTickets(business?.name || 'Restoran', tableNumber, items, cats);
          }}
        />
      )}

      {/* Yeni Sifariş Modal */}
      {showNewOrder && (
        <NewOrderModal
          business={business}
          locationId={locationId}
          categories={menuCategories}
          menuItems={menuItems}
          tables={tables}
          activeOrders={orders}
          onClose={() => setShowNewOrder(false)}
          onSubmit={(items, tableNumber, cats) => {
            printKitchenTickets(business?.name || 'Restoran', tableNumber, items, cats);
          }}
        />
      )}
    </div>
  );
}
